import * as Clipboard from "expo-clipboard";

/**
 * Copies a chart card to the clipboard as a PNG.
 *
 * Two rendering paths exist depending on what's inside `element`:
 *
 * 1. **SVG-only charts (no HTML legend)** — bar charts without a legend,
 *    histograms, …
 *    Recharts emits a real `<svg>` with explicit pixel `width`/`height`
 *    attributes. We pick the largest SVG (skipping the 8×8 legend icons),
 *    serialise it, and rasterise via a canvas. This captures the full
 *    chart even when it overflows a horizontal scroll container, which
 *    is why we don't just screenshot the DOM wrapper for these.
 *
 * 2. **HTML-rendered visuals or charts with an HTML legend** — heatmaps,
 *    matrices, grouped/line charts whose Recharts `<Legend>` lives outside
 *    the SVG as a `.recharts-legend-wrapper` div. SVG-only capture would
 *    drop the legend, so we rasterise the wrapping element via html2canvas
 *    instead (same path matrix question-result components use directly).
 *    `html2canvas` is imported dynamically so we don't pay its bundle cost
 *    on every page load.
 */
export async function copyChartToClipboard(
  element: HTMLElement,
  onSuccess: () => void
): Promise<void> {
  try {
    const allSvgs = Array.from(element.querySelectorAll("svg"));
    const hasHtmlLegend = Boolean(
      element.querySelector(".recharts-legend-wrapper")
    );
    /* HTML-only visual (e.g. Heatmap) or chart whose legend lives outside
       the SVG — rasterise the DOM wrapper so the legend is included. */
    if (!allSvgs.length || hasHtmlLegend) {
      await copyElementAsImage(element, onSuccess);
      return;
    }

    // Pick the SVG with the largest area — this is always the main chart SVG,
    // not the tiny legend icon SVGs (8×8) that Recharts renders per legend item.
    const svgElement = allSvgs.reduce((best, svg) => {
      const area =
        parseFloat(svg.getAttribute("width") ?? "0") *
        parseFloat(svg.getAttribute("height") ?? "0");
      const bestArea =
        parseFloat(best.getAttribute("width") ?? "0") *
        parseFloat(best.getAttribute("height") ?? "0");
      return area > bestArea ? svg : best;
    });

    const widthAttr = svgElement.getAttribute("width");
    const heightAttr = svgElement.getAttribute("height");

    // Prefer explicit pixel attributes (Recharts always sets these).
    // Fall back to getBoundingClientRect only for %-based / missing attrs.
    const svgWidth =
      widthAttr && !widthAttr.endsWith("%")
        ? Math.round(parseFloat(widthAttr))
        : Math.round(svgElement.getBoundingClientRect().width) || 800;

    const svgHeight =
      heightAttr && !heightAttr.endsWith("%")
        ? Math.round(parseFloat(heightAttr))
        : Math.round(svgElement.getBoundingClientRect().height) || 450;

    const cloned = svgElement.cloneNode(true) as SVGSVGElement;
    cloned.setAttribute("width", String(svgWidth));
    cloned.setAttribute("height", String(svgHeight));
    cloned.setAttribute("xmlns", "http://www.w3.org/2000/svg");
    cloned.removeAttribute("style");
    // Do NOT set preserveAspectRatio="none" — that was distorting arc charts.

    // Use a base64 data URL instead of a blob URL — more reliable across
    // browsers (Safari in particular can block blob: URLs for SVG images).
    const svgString = new XMLSerializer().serializeToString(cloned);
    const svgDataUrl = `data:image/svg+xml;base64,${btoa(
      unescape(encodeURIComponent(svgString))
    )}`;

    const img = new Image();
    img.onload = async () => {
      const scale = 2;
      const canvas = document.createElement("canvas");
      canvas.width = svgWidth * scale;
      canvas.height = svgHeight * scale;
      const ctx = canvas.getContext("2d");
      if (!ctx) return;
      ctx.scale(scale, scale);
      ctx.fillStyle = "white";
      ctx.fillRect(0, 0, svgWidth, svgHeight);
      ctx.drawImage(img, 0, 0, svgWidth, svgHeight);

      const dataURL = canvas.toDataURL("image/png");
      const base64 = dataURL.split(";base64,")[1];
      await Clipboard.setImageAsync(base64);
      onSuccess();
    };
    img.src = svgDataUrl;
  } catch (error) {
    console.error("Failed to copy chart:", error);
  }
}

/**
 * Rasterises an arbitrary HTML element via `html2canvas` and writes the
 * resulting PNG to the clipboard. Used both as the fallback path for visuals
 * that don't render as SVG (e.g. the concept-testing heatmap matrix) and as
 * the exported entry point for components that want to force image capture of
 * the DOM node regardless of any SVGs inside it.
 *
 * Visuals like the heatmap sit inside an `overflow-x-auto` container so
 * the user can horizontally scroll wide matrices, but that scroll
 * clipping is also what a naive `html2canvas(element)` would capture
 * — chopping the right-hand columns out of the PNG. We work around
 * this by:
 *  1. Probing the element + all descendants for the largest
 *     `scrollWidth` / `scrollHeight`, so the output canvas is sized
 *     to fit the *un*clipped content.
 *  2. Pumping that width through `windowWidth` so html2canvas's
 *     internal iframe renders the clone at the full natural width.
 *  3. Using `onclone` to strip `overflow` from every node in the
 *     cloned DOM, so once it's laid out the content expands to its
 *     natural width rather than scroll-clipping again.
 */
export async function copyElementAsImage(
  element: HTMLElement,
  onSuccess: () => void
): Promise<void> {
  try {
    let captureWidth = element.scrollWidth;
    let captureHeight = element.scrollHeight;
    element.querySelectorAll<HTMLElement>("*").forEach((el) => {
      captureWidth = Math.max(captureWidth, el.scrollWidth);
      captureHeight = Math.max(captureHeight, el.scrollHeight);
    });

    const { default: html2canvas } = await import("html2canvas");
    const canvas = await html2canvas(element, {
      backgroundColor: "#ffffff",
      height: captureHeight,
      onclone: (_doc, clonedRoot) => {
        if (!(clonedRoot instanceof HTMLElement)) return;
        const expand = (node: HTMLElement) => {
          node.style.overflow = "visible";
          node.style.overflowX = "visible";
          node.style.overflowY = "visible";
          node.style.maxWidth = "none";
          for (const child of Array.from(node.children)) {
            if (child instanceof HTMLElement) expand(child);
          }
        };
        expand(clonedRoot);
      },
      scale: 2,
      width: captureWidth,
      windowWidth: Math.max(window.innerWidth, captureWidth + 100),
    });
    await new Promise<void>((resolve) => {
      canvas.toBlob(async (blob) => {
        if (!blob) {
          resolve();
          return;
        }
        const reader = new FileReader();
        reader.onloadend = async () => {
          const base64 = (reader.result as string).split(",")[1];
          await Clipboard.setImageAsync(base64);
          onSuccess();
          resolve();
        };
        reader.readAsDataURL(blob);
      }, "image/png");
    });
  } catch (error) {
    console.error("Failed to copy chart:", error);
  }
}
